import React from 'react';
import { Expand, Download, Maximize2 } from 'lucide-react';
import { DASHBOARD_TAB_META as TAB_META } from '../../pages/dashboardTabs';
import { Card } from '../Card/Card';
import { KpiTile } from '../KpiTile/KpiTile';
import { SkeletonLine, ChartPlotSkeleton } from '../ReportsNative/ReportSkeleton';
import '../ReportsNative/ReportsNative.css';
import '../Forecast/TonightPickup.css';
import '../Forecast/ForecastBreakdown.css';

/**
 * New-look (inset shell) dashboard first-load skeleton.
 *
 * Built from the same components and classes the loaded dashboard renders —
 * the flush toolbar with the section tabs and briefing aside, KpiTile, the
 * shared Card chart shell, the report-kit performance table and the forecast
 * pickup tiles — so every box lands where the data will and nothing shifts
 * when it arrives. The Classic dashboard keeps DashboardSkeleton.
 */

const PERIOD_WIDTHS = [34, 18, 22, 22, 22, 28, 48];
const CHART_MIN_HEIGHT = 260;

function Toolbar({ tabs, activeTab, briefing }) {
  return (
    <div className="rn-filter-bar rn-sticky rn-tb-flush rn-sk-toolbar" aria-hidden="true">
      {tabs.length > 1 && (
        <>
          <div className="rn-tb-tabs">
            <div className="rn-tabs" role="presentation">
              {tabs.map((key) => {
                const meta = TAB_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <button key={key} type="button" tabIndex={-1} className={key === activeTab ? 'rn-active' : undefined}>
                    <Icon aria-hidden="true" />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <span className="rn-tb-sep" aria-hidden="true" />
        </>
      )}
      <div className="rn-seg">
        {PERIOD_WIDTHS.map((w, i) => (
          <button key={i} type="button" tabIndex={-1} className={i === 5 ? 'rn-active' : undefined}>
            <SkeletonLine w={w} h={10} />
          </button>
        ))}
      </div>
      <div className="date-range-picker">
        <div className="date-range-picker-input">
          <SkeletonLine w={150} h={10} />
        </div>
      </div>
      {[64, 72].map((w, i) => (
        <div className="rn-chip-wrap" key={i}>
          <div className="rn-filter-chip">
            <SkeletonLine w={w} h={10} />
          </div>
        </div>
      ))}
      <div className="rn-spacer" />
      {briefing && (
        <div className="rn-tb-aside">
          <div className="rn-briefing">
            <span className="rn-briefing-icon"><SkeletonLine w={14} h={14} /></span>
            <SkeletonLine w={96} h={9} />
            <SkeletonLine w={170} h={11} />
            <SkeletonLine w={64} h={11} />
            <SkeletonLine w={84} h={22} radius={999} />
          </div>
        </div>
      )}
    </div>
  );
}

function TileRow({ count, columns }) {
  return (
    <div className="rn-joinrow" style={{ display: 'grid', gridTemplateColumns: columns }}>
      {Array.from({ length: count }, (_, i) => (
        <KpiTile
          key={i}
          label={<SkeletonLine w={72 + ((i * 17) % 30)} h={9} />}
          value={<SkeletonLine w="62%" h={28} style={{ margin: '2px 0' }} />}
          sub={<SkeletonLine w="54%" h={9} style={{ marginTop: 6 }} />}
          comparison={undefined}
        />
      ))}
    </div>
  );
}

function ChartCard({ titleWidth = 128, legend = 2, kind = 'area' }) {
  return (
    <Card
      title={<SkeletonLine w={titleWidth} h={12} />}
      right={(
        <span className="rn-icon-btn" aria-hidden="true">
          <Expand />
        </span>
      )}
    >
      <div className="rn-dash-chart" style={{ flex: '1 1 auto', minHeight: CHART_MIN_HEIGHT }}>
        {legend > 0 && (
          <div className="rn-chart-legend">
            {Array.from({ length: legend }, (_, i) => (
              <SkeletonLine key={i} w={72 + i * 14} h={9} />
            ))}
          </div>
        )}
        <ChartPlotSkeleton height={CHART_MIN_HEIGHT - 44} kind={kind} />
      </div>
    </Card>
  );
}

function ChartRow({ cards, columns = 'minmax(0, 2fr) minmax(0, 1fr)' }) {
  return (
    <div className="rn-chartgrid" style={{ display: 'grid', gridTemplateColumns: columns }}>
      {cards.map((card, i) => <ChartCard key={i} {...card} />)}
    </div>
  );
}

function PerformanceTable({ columns = 7, rows = 6, titleWidth = 150 }) {
  const cols = Array.from({ length: columns }, (_, i) => i);
  return (
    <section className="rn-card rn-perf rn-sk-perf" aria-hidden="true">
      <div className="rn-table-toolbar">
        <h2><SkeletonLine w={titleWidth} h={12} /></h2>
        <span className="rn-pill-note"><SkeletonLine w={180} h={9} /></span>
        <div className="rn-spacer" />
        <div className="rn-seg">
          <button type="button" tabIndex={-1} className="rn-active"><SkeletonLine w={52} h={9} /></button>
          <button type="button" tabIndex={-1}><SkeletonLine w={60} h={9} /></button>
        </div>
        <div className="rn-tools">
          <span className="rn-icon-btn"><Download /></span>
          <span className="rn-icon-btn"><Maximize2 /></span>
        </div>
      </div>
      <div className="rn-table-scroll">
        <table className="rn-detail-table">
          <thead>
            <tr>
              <th><SkeletonLine w={58} h={9} /></th>
              {cols.map((c) => (
                <th key={c} className="rn-align-right"><SkeletonLine w={54 + ((c * 13) % 24)} h={9} style={{ marginLeft: 'auto' }} /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                <td className="rn-cell-strong"><SkeletonLine w={120 + ((r * 29) % 60)} h={10} /></td>
                {cols.map((c) => (
                  <td key={c} className="rn-align-right rn-cell-mono">
                    <SkeletonLine w={48 + (((r + 1) * (c + 3)) % 26)} h={10} style={{ marginLeft: 'auto' }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><SkeletonLine w={40} h={10} className="rn-sk-ink" /></td>
              {cols.map((c) => (
                <td key={c} className="rn-align-right rn-cell-mono">
                  <SkeletonLine w={58} h={10} className="rn-sk-ink" style={{ marginLeft: 'auto' }} />
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function ForecastBody() {
  return (
    <div className="tp-section">
      <div className="rn-sheet">
        <div className="tp-tiles rn-joinrow">
          {Array.from({ length: 5 }, (_, i) => (
            <div className={'tp-tile' + (i === 0 ? ' accent' : '')} key={i}>
              <div className="tp-tile-label"><SkeletonLine w={96 + ((i * 19) % 30)} h={9} /></div>
              <div className="tp-tile-value"><SkeletonLine w="58%" h={24} /></div>
              <div className="tp-tile-sub"><SkeletonLine w="70%" h={9} /></div>
              <div className="tp-tile-foot"><SkeletonLine w={48} h={14} radius={999} /></div>
            </div>
          ))}
        </div>
        <div className="tp-chart-card">
          <div className="tp-chart-head">
            <div>
              <div className="tp-chart-title"><SkeletonLine w={230} h={12} /></div>
              <div className="tp-chart-sub"><SkeletonLine w={280} h={9} style={{ marginTop: 6 }} /></div>
            </div>
            <div className="tp-chart-legend">
              <SkeletonLine w={96} h={9} />
              <SkeletonLine w={112} h={9} />
            </div>
          </div>
          <div className="tp-chart-wrap">
            <ChartPlotSkeleton height={220} axes={false} kind="bar" />
          </div>
        </div>
        <div className="fp-card">
          <div className="fp-head">
            <div>
              <h2 className="fp-title"><SkeletonLine w={140} h={12} /></h2>
              <div className="fp-sub"><SkeletonLine w={320} h={9} style={{ marginTop: 6 }} /></div>
            </div>
          </div>
          <div className="fp-scroll">
            <table className="fp-table">
              <thead>
                <tr>
                  <th className="first"><SkeletonLine w={58} h={9} /></th>
                  {Array.from({ length: 6 }, (_, c) => <th key={c}><SkeletonLine w={56} h={9} style={{ marginLeft: 'auto' }} /></th>)}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }, (_, r) => (
                  <tr key={r}>
                    <td className="first"><SkeletonLine w={120 + ((r * 23) % 50)} h={10} /></td>
                    {Array.from({ length: 6 }, (_, c) => <td key={c}><SkeletonLine w={44 + ((r + c) % 3) * 8} h={10} style={{ marginLeft: 'auto' }} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const TILE_COLUMNS = 'repeat(auto-fit, minmax(220px, 1fr))';

function TabBody({ tab }) {
  if (tab === 'forecast') return <ForecastBody />;
  if (tab === 'operations') {
    return (
      <div className="rn-sheet">
        <TileRow count={3} columns={TILE_COLUMNS} />
        <div>
          <ChartCard titleWidth={150} kind="bar" />
        </div>
        <PerformanceTable columns={8} titleWidth={200} />
      </div>
    );
  }
  if (tab === 'financial') {
    return (
      <div className="rn-sheet">
        <TileRow count={3} columns="repeat(3, 1fr)" />
        <ChartRow cards={[{ titleWidth: 150, kind: 'bar' }, { titleWidth: 90, legend: 1 }]} />
        <PerformanceTable columns={6} titleWidth={210} />
      </div>
    );
  }
  return (
    <div className="rn-sheet">
      <TileRow count={5} columns={TILE_COLUMNS} />
      <ChartRow cards={[{ titleWidth: 120, legend: 3 }, { titleWidth: 120, legend: 1 }]} />
      <PerformanceTable columns={7} titleWidth={170} />
    </div>
  );
}

export function DashboardNewSkeleton({ activeTab = 'revenue', tabs = ['overview', 'revenue', 'forecast', 'operations', 'financial'], briefing = true }) {
  const tab = TAB_META[activeTab] ? activeTab : 'revenue';
  return (
    <div className="dashboard-root">
      <div
        className="rn-page dashboard-page rn-sk-page"
        style={{ maxWidth: 1760 }}
        aria-busy="true"
        aria-label="Loading dashboard"
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Toolbar tabs={tabs} activeTab={tab} briefing={briefing} />
          <div style={{ marginBottom: 32 }}>
            <TabBody tab={tab} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardNewSkeleton;
